import React, { ReactNode, useCallback } from 'react';
import { BaseSidebar } from '@experiences/containers/sidebar/base-sidebar';
import { createStyles } from '@/contexts/theme-context';
import { Drawer } from '@usepepper/sprout-kit-ui';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { NavItemSkeleton } from '@experiences/components/nav-item-skeleton';
import { Platform, View } from 'react-native';
import { SidebarNavGroup } from '@experiences/components/sidebar-nav-group';
import { SidebarNavItem } from '@experiences/components/sidebar-nav-item';
import { SideDrawer } from '@experiences/components/side-drawer';
import { SidenavItem, SidenavSingleItem } from '@/domain/navigation/types';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useShallow } from 'zustand/shallow';

export const DEFAULT_SIDEBAR_WIDTH = 232;

const SAFE_AREA_EDGES: Edge[] =
  Platform.OS === 'android'
    ? ['top', 'bottom', 'left', 'right']
    : ['top', 'left', 'right'];

const SKELETON_COUNT = 3;

interface SidebarLayoutShellProps {
  children: ReactNode;
  navItems: SidenavItem[];
  navItemsLoading?: boolean;
  sidebarFooter?: ReactNode;
  sidebarWidth?: number;
}

/**
 * Sidebar + responsive drawer + main-content slot, with no top bar chrome.
 *
 * Composed by:
 * - `BaseSidebarTemplate` — adds a top bar above children.
 * - `AppShellTemplate` — renders children directly, flush against the
 *   sidebar. Used when the screen (or embedded microproduct) ships its
 *   own `<PageLayout>` chrome.
 *
 * Responsive behavior:
 * - LARGE / MEDIUM: inline sidebar, uses persisted collapse state
 * - SMALL_TABLET (iPad portrait): drawer overlay, same as mobile
 * - SMALL (mobile): sidebar rendered as a slide-over drawer overlay
 */
export const SidebarLayoutShell: React.FC<SidebarLayoutShellProps> = ({
  children,
  navItems,
  navItemsLoading = false,
  sidebarFooter,
  sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
}) => {
  const [styles] = useStyles();
  const { layoutSize } = useLayoutContext();
  const useDrawerLayout =
    layoutSize === LayoutSize.SMALL || layoutSize === LayoutSize.SMALL_TABLET;

  // State reads via `useShallow` so we only re-render when a tracked key
  // actually changes. Action references (toggle*, set*) are stable in
  // zustand so pulling them from `getState()` on demand avoids extra
  // subscriptions on the hot path.
  const { isSidebarCollapsed, isDrawerOpen } = useExperienceUIStore(
    useShallow((state) => ({
      isDrawerOpen: state.isMobileDrawerOpen,
      isSidebarCollapsed: state.isSidebarCollapsed,
    })),
  );

  const handleToggleCollapse = useCallback(() => {
    const { toggleSidebar, toggleMobileDrawer } =
      useExperienceUIStore.getState();
    if (useDrawerLayout) {
      toggleMobileDrawer();
    } else {
      toggleSidebar();
    }
  }, [useDrawerLayout]);

  const setIsDrawerOpen = useCallback((open: boolean) => {
    useExperienceUIStore.getState().setMobileDrawerOpen(open);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, [setIsDrawerOpen]);

  const renderNavItems = () => {
    if (navItemsLoading) {
      return Array.from({ length: SKELETON_COUNT }).map((_, index) => {
        const opacity = index === 1 ? 0.4 : index === 2 ? 0.2 : 1;
        return <NavItemSkeleton key={index} style={{ opacity }} />;
      });
    }
    const handleNavigate = (item: SidenavSingleItem) => {
      item.onPress();
      // Close drawer after navigating so it doesn't stay open.
      if (useDrawerLayout) {
        handleToggleCollapse();
      }
    };

    return navItems.map((item) => {
      if (item.subnavItems) {
        return item.subnavItems.length > 0 ? (
          <SidebarNavGroup
            item={item}
            key={item.id ?? item.label}
            onSubnavItemPress={handleNavigate}
          />
        ) : null;
      }

      return (
        <SidebarNavItem
          active={item.active}
          icon={item.icon}
          iconUrl={item.iconUrl}
          key={item.id ?? item.label}
          label={item.label}
          onPress={() => handleNavigate(item)}
        />
      );
    });
  };

  const sidebarContent = (
    <BaseSidebar footer={sidebarFooter} onToggleCollapse={handleToggleCollapse}>
      {renderNavItems()}
    </BaseSidebar>
  );

  return (
    <SafeAreaView edges={SAFE_AREA_EDGES} style={styles.safeArea}>
      <View style={useDrawerLayout ? styles.containerMobile : styles.container}>
        {!useDrawerLayout ? (
          <View
            style={[
              styles.sidebar,
              { width: sidebarWidth },
              isSidebarCollapsed && styles.sidebarCollapsed,
            ]}
          >
            {sidebarContent}
          </View>
        ) : null}
        <View style={styles.mainArea}>{children}</View>
      </View>

      {/* Slide-over drawer for mobile + tablet portrait.
          sprout-kit-ui Drawer uses Tamagui translateX which crashes on native
          (RN expects number, Tamagui emits CSS string). Use SideDrawer on native. */}
      {useDrawerLayout ? (
        Platform.OS === 'web' ? (
          <Drawer onOpenChange={setIsDrawerOpen} open={isDrawerOpen}>
            <Drawer.Overlay />
            <Drawer.Content width={sidebarWidth}>
              {sidebarContent}
            </Drawer.Content>
          </Drawer>
        ) : (
          <SideDrawer
            direction="left"
            isOpen={isDrawerOpen}
            onClose={handleCloseDrawer}
            showCloseButton={false}
            width={sidebarWidth}
          >
            {sidebarContent}
          </SideDrawer>
        )
      ) : null}
    </SafeAreaView>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  containerMobile: {
    flex: 1,
  },
  mainArea: {
    flex: 1,
  },
  safeArea: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flex: 1,
  },
  sidebar: {
    alignSelf: 'stretch',
    overflow: 'hidden',
    transition: 'width 200ms cubic-bezier(0.33, 1, 0.68, 1)',
  },
  sidebarCollapsed: {
    width: 0,
  },
}));
